import { FC, useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { DispatchGuideDto } from '../../interfaces'
import { guideKey } from '../../utils/buildDispatchPayload'
import { rangeBetween } from '../../utils/shipmentSelection'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import moment from 'moment'
import { FormCheckbox } from '../FormFields/FormCheckbox'

export enum GuideDispatchField {
  NUM_GUIDE,
  SERVICE,
  ORIGEN,
  PESO,
  VOLUMEN,
  PIECES,
  DATECREATE,
  SALE,
}

interface ShipmentTablesProps {
  selectedAll: boolean
  dispatchGuides: DispatchGuideDto[]
  /**
   * Claves de las guías seleccionadas (ver `guideKey`).
   *
   * Antes era el array entero y cada fila hacía `.some()` sobre él: con 5000
   * guías tildadas y 100 filas en pantalla son 500.000 comparaciones por render.
   * Un Set lo deja en una consulta por fila.
   */
  selectedKeys: Set<string>
  sortBy: { field: GuideDispatchField; asc: boolean }
  onChangeSortBy: (field: GuideDispatchField) => void
  onSelectGuide: (guide: DispatchGuideDto) => void
  onSelectAll: () => void
  /**
   * Marca o desmarca un lote de golpe (rango con Shift, arrastre).
   * Opcional: sin esto la tabla se comporta como antes, un click = una fila.
   */
  onSetGuidesSelected?: (guides: DispatchGuideDto[], selected: boolean) => void
}

export const GuidesDispatchTables: FC<ShipmentTablesProps> = ({
  selectedAll,
  dispatchGuides,
  selectedKeys,
  sortBy,
  onChangeSortBy,
  onSelectGuide,
  onSelectAll,
  onSetGuidesSelected,
}) => {
  /** Última fila marcada sin Shift. Es el extremo fijo del rango. */
  const [anchorIndex, setAnchorIndex] = useState<number | null>(null)
  /** Arrastre por la columna del checkbox: pinta con la operación que arrancó. */
  const dragMode = useRef<'select' | 'deselect' | null>(null)

  // El rango se calcula sobre el orden que el usuario VE. Si cambia el orden, la
  // página o el resultado de la búsqueda, el ancla ya no apunta a la misma guía:
  // "de la 1 a la 10" marcaría filas que no son esas. Se descarta.
  useEffect(() => {
    setAnchorIndex(null)
  }, [dispatchGuides])

  // El arrastre termina aunque se suelte el botón fuera de la tabla.
  useEffect(() => {
    const endDrag = () => {
      dragMode.current = null
    }
    window.addEventListener('mouseup', endDrag)
    return () => window.removeEventListener('mouseup', endDrag)
  }, [])

  const isSelected = (guide: DispatchGuideDto) =>
    selectedKeys.has(guideKey(guide))

  const applySelection = (
    e: { shiftKey: boolean },
    guide: DispatchGuideDto,
    index: number
  ) => {
    // Shift + click marca todo el tramo entre el ancla y esta fila, como en
    // Excel o el explorador de archivos. Ctrl no hace falta: en una lista de
    // checkboxes el click simple ya es aditivo.
    if (e.shiftKey && anchorIndex !== null && onSetGuidesSelected) {
      onSetGuidesSelected(
        rangeBetween(dispatchGuides, anchorIndex, index),
        true
      )
      return
    }

    onSelectGuide(guide)
    setAnchorIndex(index)
  }

  const handleSelectMouseDown = (
    e: React.MouseEvent,
    guide: DispatchGuideDto,
    index: number
  ) => {
    e.stopPropagation()
    // Evita que el navegador arranque una selección de texto al arrastrar.
    e.preventDefault()

    applySelection(e, guide, index)

    // Con Shift el gesto es un rango, no un arrastre.
    if (!e.shiftKey) {
      dragMode.current = isSelected(guide) ? 'deselect' : 'select'
    }
  }

  /** Pinta las filas por las que pasa el mouse con el botón apretado. */
  const handleSelectMouseEnter = (guide: DispatchGuideDto) => {
    if (!dragMode.current || !onSetGuidesSelected) return

    const shouldSelect = dragMode.current === 'select'
    if (isSelected(guide) !== shouldSelect) {
      onSetGuidesSelected([guide], shouldSelect)
    }
  }

  // Teclado: Tab enfoca el checkbox y Espacio dispara un click sintético, que
  // llega con `detail === 0`. Los clicks de mouse ya se atendieron en mousedown.
  const handleSelectClick = (
    e: React.MouseEvent,
    guide: DispatchGuideDto,
    index: number
  ) => {
    e.stopPropagation()
    if (e.detail === 0) applySelection(e, guide, index)
  }

  return (
    <div className="relative w-full table-fix-head mt-4 overflow-auto">
      <table className="table-auto w-full text-gray-600 text-sm text-left border border-gray-300">
        <thead className="select-none font-semibold border-b border-gray-300 bg-gray-50">
          <tr>
            <th className="pl-2 py-2">
              <FormCheckbox
                id="selectAll"
                name="selectAll"
                label=""
                onChange={onSelectAll}
                checked={selectedAll}
              />
            </th>

            <th
              className="px-4 py-2"
              onClick={() => onChangeSortBy(GuideDispatchField.NUM_GUIDE)}
              style={{ minWidth: '12rem' }}
            >
              <div className="flex gap-2 items-center cursor-pointer">
                <span>NÚMERO DE GUÍA</span>
                <div className="h-4 w-4">
                  {sortBy.field == GuideDispatchField.NUM_GUIDE && (
                    <ChevronDownIcon
                      className={classNames(
                        'w-4 h-4',
                        sortBy.asc && 'rotate-180'
                      )}
                    />
                  )}
                </div>
              </div>
            </th>

            {/* <th
              className="px-4 py-2"
              onClick={() => onChangeSortBy(GuideDispatchField.SERVICE)}
              style={{ minWidth: "2rem" }}
            >
              <div className="flex gap-2 items-center cursor-pointer">
                <span>SERVICIO</span>
                <div className="h-4 w-4">
                  {sortBy.field == GuideDispatchField.SERVICE && (
                    <ChevronDownIcon
                      className={classNames("w-4 h-4", sortBy.asc && "rotate-180")}
                    />
                  )}
                </div>
              </div>
            </th> */}

            <th
              className="px-4 py-2"
              onClick={() => onChangeSortBy(GuideDispatchField.ORIGEN)}
            >
              <div className="flex gap-2 items-center cursor-pointer">
                <span>ORIGEN</span>
                <div className="h-4 w-4">
                  {sortBy.field == GuideDispatchField.ORIGEN && (
                    <ChevronDownIcon
                      className={classNames(
                        'w-4 h-4',
                        sortBy.asc && 'rotate-180'
                      )}
                    />
                  )}
                </div>
              </div>
            </th>
            <th className="px-4 py-2">
              <div className="flex gap-2 items-center cursor-pointer">
                <span>DESTINO</span>
              </div>
            </th>

            <th
              className="px-4 py-2"
              onClick={() => onChangeSortBy(GuideDispatchField.PESO)}
            >
              <div className="flex gap-2 items-center cursor-pointer">
                <span>PESO FÍSICO</span>
                <div className="h-4 w-4">
                  {sortBy.field == GuideDispatchField.PESO && (
                    <ChevronDownIcon
                      className={classNames(
                        'w-4 h-4',
                        sortBy.asc && 'rotate-180'
                      )}
                    />
                  )}
                </div>
              </div>
            </th>

            <th
              className="px-4 py-2"
              onClick={() => onChangeSortBy(GuideDispatchField.VOLUMEN)}
              style={{ minWidth: '5rem' }}
            >
              <div className="flex gap-2 items-center cursor-pointer">
                <span>VOLUMEN</span>
                <div className="h-4 w-4">
                  {sortBy.field == GuideDispatchField.VOLUMEN && (
                    <ChevronDownIcon
                      className={classNames(
                        'w-4 h-4',
                        sortBy.asc && 'rotate-180'
                      )}
                    />
                  )}
                </div>
              </div>
            </th>

            <th
              className="px-4 py-2"
              onClick={() => onChangeSortBy(GuideDispatchField.PIECES)}
            >
              <div className="flex gap-2 items-center cursor-pointer truncate">
                <span>TOTAL PIEZAS</span>
                <div className="h-4 w-4">
                  {sortBy.field == GuideDispatchField.PIECES && (
                    <ChevronDownIcon
                      className={classNames(
                        'w-4 h-4',
                        sortBy.asc && 'rotate-180'
                      )}
                    />
                  )}
                </div>
              </div>
            </th>
            <th
              className="px-4 py-2"
              onClick={() => onChangeSortBy(GuideDispatchField.SALE)}
            >
              <div className="flex gap-2 items-center cursor-pointer truncate">
                <span>GUÍA ALIADA</span>
                <div className="h-4 w-4">
                  {sortBy.field == GuideDispatchField.SALE && (
                    <ChevronDownIcon
                      className={classNames(
                        'w-4 h-4',
                        sortBy.asc && 'rotate-180'
                      )}
                    />
                  )}
                </div>
              </div>
            </th>

            <th
              className="px-4 py-2"
              onClick={() => onChangeSortBy(GuideDispatchField.DATECREATE)}
            >
              <div className="flex gap-2 items-center cursor-pointer truncate">
                <span>FECHA CREACIÓN</span>
                <div className="h-4 w-4">
                  {sortBy.field == GuideDispatchField.DATECREATE && (
                    <ChevronDownIcon
                      className={classNames(
                        'w-4 h-4',
                        sortBy.asc && 'rotate-180'
                      )}
                    />
                  )}
                </div>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {dispatchGuides.map((dispatchGuide, index) => {
            const selected = selectedKeys.has(guideKey(dispatchGuide))

            return (
              <tr
                key={dispatchGuide.numGuia}
                className={classNames(
                  'hover:bg-gray-200',
                  index % 2 == 0 && 'bg-gray-100',
                  selected && '!bg-blue-200'
                )}
              >
                <th
                  className="pl-2 py-2 select-none"
                  onMouseDown={(e) =>
                    handleSelectMouseDown(e, dispatchGuide, index)
                  }
                  onMouseEnter={() => handleSelectMouseEnter(dispatchGuide)}
                  onClick={(e) => handleSelectClick(e, dispatchGuide, index)}
                >
                  <FormCheckbox
                    id={`select-${dispatchGuide.numGuia}-${index}`}
                    name={`select-${dispatchGuide.numGuia}-${index}`}
                    label=""
                    // La celda es la que decide (necesita shiftKey y el índice);
                    // el input queda controlado por `checked`.
                    onChange={() => {}}
                    checked={selected}
                  />
                </th>
                <td className="px-4 py-1 font-semibold">
                  {dispatchGuide.numGuia}
                </td>
                {/* <td className="px-4 py-1">{dispatchGuide.servicioID}</td> */}
                <td className="px-4 py-1">{dispatchGuide.origen} </td>
                <td className="px-4 py-1">{dispatchGuide.ciudadDestino} </td>
                <td className="px-4 py-1">{dispatchGuide.peso}</td>
                <td className="px-4 py-1">{dispatchGuide.volumen ?? '-'}</td>
                <td className="px-4 py-1">
                  {dispatchGuide.totalPiezas} Pieza
                  {(dispatchGuide.totalPiezas ?? 0) >= 1 ? 's' : ''}
                </td>
                <td className="px-4 py-1">{dispatchGuide.sealNumber}</td>
                <td className="px-4 py-1">
                  {moment(dispatchGuide.fechaCreacion).format('DD/MM/YYYY')}
                </td>
              </tr>
            )
          })}

          {dispatchGuides.length == 0 && (
            <tr>
              <td colSpan={7} className="px-4 py-4 text-center">
                Parece que aún no tienes preguías.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}
